import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  BoltIcon,
  PlusIcon,
  TrashIcon,
  PencilSquareIcon,
  ArrowPathIcon,
  PaperAirplaneIcon,
  ClipboardDocumentIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowUturnLeftIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { Modal } from '../ui/Modal';
import { Spinner } from '../ui/Spinner';
import { EmptyState } from '../ui/EmptyState';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { webhookService } from '../../services/webhook.service';
import type {
  CreateWebhookData,
  WebhookDelivery,
  WebhookEndpoint,
  WebhookEventDefinition,
} from '../../types';

const CATEGORY_LABELS: Record<string, string> = {
  patient: 'Patients',
  order: 'Orders',
  clinical: 'Clinical review',
  payment: 'Payments',
  subscription: 'Subscriptions',
  fulfillment: 'Pharmacy fulfillment',
  shipment: 'Shipping',
};

const DELIVERY_STATUS_VARIANT: Record<string, 'success' | 'danger' | 'warning' | 'secondary'> = {
  delivered: 'success',
  failed: 'danger',
  pending: 'warning',
  abandoned: 'secondary',
};

/** Pulls the API's `error` string out of an axios failure without an `any` cast. */
const apiErrorMessage = (error: unknown, fallback: string): string => {
  const response = (error as { response?: { data?: { error?: unknown } } })?.response;
  const message = response?.data?.error;
  return typeof message === 'string' ? message : fallback;
};

const formatDateTime = (value: string | null): string =>
  value ? new Date(value).toLocaleString() : '—';

const copyToClipboard = (text: string, label = 'Copied to clipboard') => {
  navigator.clipboard.writeText(text);
  toast.success(label);
};

/* -------------------------------------------------------------------------- */
/*  Event subscription picker                                                 */
/* -------------------------------------------------------------------------- */

interface EventPickerProps {
  catalog: WebhookEventDefinition[];
  /** `['*']` means "everything, including event types added later". */
  value: string[];
  onChange: (next: string[]) => void;
}

function EventPicker({ catalog, value, onChange }: EventPickerProps) {
  const subscribesToAll = value.includes('*');

  const grouped = useMemo(() => {
    return catalog.reduce<Record<string, WebhookEventDefinition[]>>((acc, event) => {
      (acc[event.category] ||= []).push(event);
      return acc;
    }, {});
  }, [catalog]);

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const toggleEvent = (type: string) => {
    // Leaving "all events" materialises the current catalog so the explicit
    // selection is a real list rather than a wildcard minus one.
    const base = subscribesToAll ? catalog.map((e) => e.type) : value;
    const next = base.includes(type) ? base.filter((t) => t !== type) : [...base, type];
    onChange(next.length === 0 ? [] : next);
  };

  const toggleCategory = (category: string) => {
    const categoryTypes = grouped[category].map((e) => e.type);
    const base = subscribesToAll ? catalog.map((e) => e.type) : value;
    const allSelected = categoryTypes.every((type) => base.includes(type));
    const next = allSelected
      ? base.filter((type) => !categoryTypes.includes(type))
      : Array.from(new Set([...base, ...categoryTypes]));
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <label className="flex items-start gap-3 p-3 rounded-lg border border-secondary-200 dark:border-secondary-700 cursor-pointer">
        <input
          type="checkbox"
          checked={subscribesToAll}
          onChange={(e) => onChange(e.target.checked ? ['*'] : catalog.map((event) => event.type))}
          className="mt-1 h-4 w-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
        />
        <span>
          <span className="block text-sm font-medium text-secondary-900 dark:text-secondary-100">
            Send all events
          </span>
          <span className="block text-xs text-secondary-500 dark:text-secondary-400">
            Recommended. Your endpoint automatically receives any new event types we add.
          </span>
        </span>
      </label>

      {!subscribesToAll && (
        <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
          {Object.entries(grouped).map(([category, events]) => {
            const selectedCount = events.filter((event) => value.includes(event.type)).length;
            const isOpen = expanded[category] ?? selectedCount > 0;

            return (
              <div key={category} className="border border-secondary-200 dark:border-secondary-700 rounded-lg">
                <div className="flex items-center justify-between px-3 py-2 bg-secondary-50 dark:bg-secondary-800 rounded-t-lg">
                  <button
                    type="button"
                    onClick={() => setExpanded((prev) => ({ ...prev, [category]: !isOpen }))}
                    className="flex items-center gap-2 text-sm font-medium text-secondary-900 dark:text-secondary-100"
                  >
                    {isOpen ? (
                      <ChevronDownIcon className="w-4 h-4" />
                    ) : (
                      <ChevronRightIcon className="w-4 h-4" />
                    )}
                    {CATEGORY_LABELS[category] ?? category}
                    <Badge variant="secondary" className="text-[10px]">
                      {selectedCount}/{events.length}
                    </Badge>
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleCategory(category)}
                    className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    {selectedCount === events.length ? 'Clear' : 'Select all'}
                  </button>
                </div>

                {isOpen && (
                  <div className="p-2 space-y-1">
                    {events.map((event) => (
                      <label
                        key={event.type}
                        className="flex items-start gap-3 p-2 rounded hover:bg-secondary-50 dark:hover:bg-secondary-700/50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={value.includes(event.type)}
                          onChange={() => toggleEvent(event.type)}
                          className="mt-0.5 h-4 w-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="min-w-0">
                          <code className="block text-xs font-mono text-primary-600 dark:text-primary-400">
                            {event.type}
                          </code>
                          <span className="block text-xs text-secondary-500 dark:text-secondary-400">
                            {event.description}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Endpoint form modal                                                       */
/* -------------------------------------------------------------------------- */

interface EndpointModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateWebhookData) => Promise<void>;
  catalog: WebhookEventDefinition[];
  endpoint: WebhookEndpoint | null;
  isSaving: boolean;
}

// Mounted fresh per open (see the `key` at the call site), so the form state can
// simply be initialised from the endpoint being edited.
function EndpointModal({ isOpen, onClose, onSubmit, catalog, endpoint, isSaving }: EndpointModalProps) {
  const [url, setUrl] = useState(endpoint?.url ?? '');
  const [description, setDescription] = useState(endpoint?.description ?? '');
  const [subscribedEvents, setSubscribedEvents] = useState<string[]>(endpoint?.subscribedEvents ?? ['*']);
  const [urlError, setUrlError] = useState('');

  const handleSubmit = async () => {
    const trimmed = url.trim();

    if (!trimmed) {
      setUrlError('A destination URL is required');
      return;
    }

    try {
      const parsed = new URL(trimmed);
      if (parsed.protocol !== 'https:') {
        setUrlError('The URL must use HTTPS');
        return;
      }
    } catch {
      setUrlError('That is not a valid URL');
      return;
    }

    if (subscribedEvents.length === 0) {
      toast.error('Select at least one event, or choose "Send all events"');
      return;
    }

    await onSubmit({ url: trimmed, description: description.trim() || undefined, subscribedEvents });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={endpoint ? 'Edit webhook endpoint' : 'Add webhook endpoint'}
      description="We POST a signed JSON payload to this URL every time a subscribed event happens."
      size="xl"
    >
      <div className="space-y-5">
        <Input
          label="Endpoint URL"
          placeholder="https://api.yourcompany.com/webhooks/capture-health"
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            setUrlError('');
          }}
          error={urlError}
          helperText="Must be HTTPS and publicly reachable. Respond with a 2xx within 10 seconds."
        />

        <Input
          label="Description (optional)"
          placeholder="Affiliate tracking — production"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />

        <div>
          <span className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
            Events
          </span>
          <EventPicker catalog={catalog} value={subscribedEvents} onChange={setSubscribedEvents} />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} isLoading={isSaving}>
            {endpoint ? 'Save changes' : 'Create endpoint'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/*  Delivery log                                                              */
/* -------------------------------------------------------------------------- */

function DeliveryLog({ webhookId }: { webhookId: string }) {
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [redeliveringId, setRedeliveringId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await webhookService.getDeliveries({ webhookId, pageSize: 20 });
      setDeliveries(result.data);
    } catch {
      toast.error('Failed to load delivery history');
    } finally {
      setIsLoading(false);
    }
  }, [webhookId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRedeliver = async (deliveryId: string) => {
    setRedeliveringId(deliveryId);
    try {
      await webhookService.redeliver(deliveryId);
      toast.success('Queued for redelivery');
      await load();
    } catch {
      toast.error('Failed to queue redelivery');
    } finally {
      setRedeliveringId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Spinner size="md" />
      </div>
    );
  }

  if (!deliveries.length) {
    return (
      <p className="text-sm text-secondary-500 dark:text-secondary-400 py-4 text-center">
        No deliveries yet. Send a test event to verify your endpoint.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {deliveries.map((delivery) => (
        <div
          key={delivery.id}
          className="border border-secondary-200 dark:border-secondary-700 rounded-lg overflow-hidden"
        >
          <div className="flex items-center gap-3 px-3 py-2 text-sm">
            <Badge variant={DELIVERY_STATUS_VARIANT[delivery.status] ?? 'secondary'} className="text-[10px]">
              {delivery.responseStatus ?? delivery.status}
            </Badge>
            <code className="font-mono text-xs text-secondary-900 dark:text-secondary-100 flex-1 truncate">
              {delivery.eventType}
            </code>
            <span className="text-xs text-secondary-500 dark:text-secondary-400 whitespace-nowrap hidden sm:inline">
              {formatDateTime(delivery.createdAt)}
            </span>
            <span className="text-xs text-secondary-400 dark:text-secondary-500 whitespace-nowrap">
              {delivery.attemptCount} {delivery.attemptCount === 1 ? 'attempt' : 'attempts'}
            </span>
            <button
              type="button"
              onClick={() => setExpandedId(expandedId === delivery.id ? null : delivery.id)}
              className="text-xs text-primary-600 dark:text-primary-400 hover:underline whitespace-nowrap"
            >
              {expandedId === delivery.id ? 'Hide' : 'Payload'}
            </button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleRedeliver(delivery.id)}
              isLoading={redeliveringId === delivery.id}
              title="Resend this event"
            >
              <ArrowUturnLeftIcon className="w-4 h-4" />
            </Button>
          </div>

          {expandedId === delivery.id && (
            <div className="border-t border-secondary-200 dark:border-secondary-700 p-3 space-y-3 bg-secondary-50 dark:bg-secondary-800">
              {delivery.error && (
                <p className="text-xs text-danger-600 dark:text-danger-400">{delivery.error}</p>
              )}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-secondary-600 dark:text-secondary-300">
                    Request body
                  </span>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(JSON.stringify(delivery.payload, null, 2))}
                    className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    Copy
                  </button>
                </div>
                <pre className="text-xs overflow-x-auto p-3 rounded bg-secondary-900 text-secondary-100 max-h-72">
                  <code>{JSON.stringify(delivery.payload, null, 2)}</code>
                </pre>
              </div>
              {delivery.responseBody && (
                <div>
                  <span className="block text-xs font-medium text-secondary-600 dark:text-secondary-300 mb-1">
                    Response body
                  </span>
                  <pre className="text-xs overflow-x-auto p-3 rounded bg-secondary-900 text-secondary-100 max-h-40">
                    <code>{delivery.responseBody}</code>
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Endpoint row                                                              */
/* -------------------------------------------------------------------------- */

interface EndpointRowProps {
  endpoint: WebhookEndpoint;
  onEdit: () => void;
  onDelete: () => void;
  onToggleEnabled: (enabled: boolean) => void;
  onRotateSecret: () => void;
  onTest: () => void;
  isBusy: boolean;
}

function EndpointRow({
  endpoint,
  onEdit,
  onDelete,
  onToggleEnabled,
  onRotateSecret,
  onTest,
  isBusy,
}: EndpointRowProps) {
  const [showLog, setShowLog] = useState(false);

  const healthBadge = !endpoint.enabled
    ? { variant: 'secondary' as const, label: endpoint.autoDisabledAt ? 'Auto-disabled' : 'Disabled' }
    : endpoint.consecutiveFailures > 0
    ? { variant: 'danger' as const, label: `${endpoint.consecutiveFailures} failing` }
    : { variant: 'success' as const, label: 'Healthy' };

  const eventSummary = endpoint.subscribedEvents.includes('*')
    ? 'All events'
    : `${endpoint.subscribedEvents.length} event${endpoint.subscribedEvents.length === 1 ? '' : 's'}`;

  return (
    <div className="border border-secondary-200 dark:border-secondary-700 rounded-lg overflow-hidden">
      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <code className="font-mono text-sm text-secondary-900 dark:text-secondary-100 break-all">
                {endpoint.url}
              </code>
              <Badge variant={healthBadge.variant} className="text-[10px]">
                {healthBadge.label}
              </Badge>
            </div>
            {endpoint.description && (
              <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-0.5">
                {endpoint.description}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-secondary-500 dark:text-secondary-400">
              <span>{eventSummary}</span>
              <span className="flex items-center gap-1">
                <CheckCircleIcon className="w-3.5 h-3.5" />
                Last success {formatDateTime(endpoint.lastSuccessAt)}
              </span>
              {endpoint.lastFailureAt && (
                <span className="flex items-center gap-1 text-danger-600 dark:text-danger-400">
                  <XCircleIcon className="w-3.5 h-3.5" />
                  Last failure {formatDateTime(endpoint.lastFailureAt)}
                </span>
              )}
            </div>
          </div>

          <label className="flex items-center gap-2 shrink-0 cursor-pointer">
            <input
              type="checkbox"
              checked={endpoint.enabled}
              onChange={(e) => onToggleEnabled(e.target.checked)}
              disabled={isBusy}
              className="h-4 w-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-secondary-600 dark:text-secondary-300">Enabled</span>
          </label>
        </div>

        {endpoint.autoDisabledAt && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800">
            <ExclamationTriangleIcon className="w-4 h-4 shrink-0 mt-0.5 text-warning-600 dark:text-warning-400" />
            <p className="text-xs text-warning-800 dark:text-warning-300">
              We disabled this endpoint on {formatDateTime(endpoint.autoDisabledAt)} after repeated
              delivery failures. Fix the receiver, then re-enable it to resume delivery.
              {endpoint.lastError && <> Last error: <span className="font-mono">{endpoint.lastError}</span></>}
            </p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button variant="secondary" size="sm" onClick={onTest} disabled={isBusy}>
            <PaperAirplaneIcon className="w-4 h-4 mr-1.5" />
            Send test
          </Button>
          <Button variant="secondary" size="sm" onClick={onEdit} disabled={isBusy}>
            <PencilSquareIcon className="w-4 h-4 mr-1.5" />
            Edit
          </Button>
          <Button variant="secondary" size="sm" onClick={onRotateSecret} disabled={isBusy}>
            <ArrowPathIcon className="w-4 h-4 mr-1.5" />
            Rotate secret
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowLog(!showLog)} disabled={isBusy}>
            <ClockIcon className="w-4 h-4 mr-1.5" />
            {showLog ? 'Hide deliveries' : 'Recent deliveries'}
          </Button>
          <div className="flex-1" />
          <Button variant="danger" size="sm" onClick={onDelete} disabled={isBusy}>
            <TrashIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {showLog && (
        <div className="border-t border-secondary-200 dark:border-secondary-700 p-4 bg-secondary-50 dark:bg-secondary-800">
          <DeliveryLog webhookId={endpoint.id} />
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section                                                                   */
/* -------------------------------------------------------------------------- */

export function WebhooksSection() {
  const [endpoints, setEndpoints] = useState<WebhookEndpoint[]>([]);
  const [catalog, setCatalog] = useState<WebhookEventDefinition[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<WebhookEndpoint | null>(null);

  // The plaintext secret is only ever available in the response that produced it.
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<WebhookEndpoint | null>(null);
  const [rotateTarget, setRotateTarget] = useState<WebhookEndpoint | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [endpointList, eventCatalog] = await Promise.all([
        webhookService.getWebhooks(),
        webhookService.getEventCatalog(),
      ]);
      setEndpoints(endpointList);
      setCatalog(eventCatalog.events);
    } catch {
      toast.error('Failed to load webhook configuration');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubmit = async (data: CreateWebhookData) => {
    setIsSaving(true);
    try {
      if (editing) {
        await webhookService.updateWebhook(editing.id, data);
        toast.success('Webhook endpoint updated');
      } else {
        const created = await webhookService.createWebhook(data);
        setRevealedSecret(created.secret);
        toast.success('Webhook endpoint created');
      }
      setIsModalOpen(false);
      setEditing(null);
      await load();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to save webhook endpoint'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleEnabled = async (endpoint: WebhookEndpoint, enabled: boolean) => {
    setBusyId(endpoint.id);
    try {
      await webhookService.updateWebhook(endpoint.id, { enabled });
      toast.success(enabled ? 'Endpoint enabled' : 'Endpoint disabled');
      await load();
    } catch {
      toast.error('Failed to update endpoint');
    } finally {
      setBusyId(null);
    }
  };

  const handleTest = async (endpoint: WebhookEndpoint) => {
    setBusyId(endpoint.id);
    try {
      const result = await webhookService.sendTestEvent(endpoint.id);
      if (result.success) {
        toast.success(`Endpoint responded ${result.delivery.responseStatus} in ${result.delivery.durationMs}ms`);
      } else {
        toast.error(result.delivery.error ?? 'Test delivery failed');
      }
      await load();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to send test event'));
    } finally {
      setBusyId(null);
    }
  };

  const handleRotate = async () => {
    if (!rotateTarget) return;
    setBusyId(rotateTarget.id);
    try {
      const { secret } = await webhookService.rotateSecret(rotateTarget.id);
      setRevealedSecret(secret);
      setRotateTarget(null);
      toast.success('Signing secret rotated');
      await load();
    } catch {
      toast.error('Failed to rotate signing secret');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    try {
      await webhookService.deleteWebhook(deleteTarget.id);
      setDeleteTarget(null);
      toast.success('Webhook endpoint deleted');
      await load();
    } catch {
      toast.error('Failed to delete endpoint');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card padding="none">
      <div className="p-6 border-b border-gray-200 dark:border-secondary-700">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 dark:bg-primary-900/30 rounded-lg">
              <BoltIcon className="w-6 h-6 text-primary dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold dark:text-secondary-100">Webhooks</h2>
              <p className="text-sm text-gray-500 dark:text-secondary-400">
                Receive real-time events as your customers move through checkout, clinical review,
                billing and fulfillment
              </p>
            </div>
          </div>
          <Button onClick={() => { setEditing(null); setIsModalOpen(true); }} disabled={isLoading}>
            <PlusIcon className="w-4 h-4 mr-1.5" />
            Add endpoint
          </Button>
        </div>
      </div>

      <div className="p-6">
        {revealedSecret && (
          <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
            <div className="flex items-start gap-3">
              <ShieldCheckIcon className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-green-800 dark:text-green-300">
                  Your signing secret (only shown once):
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <code className="flex-1 p-2 bg-white dark:bg-secondary-800 rounded border border-green-200 dark:border-green-700 font-mono text-sm break-all dark:text-secondary-200">
                    {revealedSecret}
                  </code>
                  <Button variant="secondary" size="sm" onClick={() => copyToClipboard(revealedSecret)}>
                    <ClipboardDocumentIcon className="w-4 h-4" />
                  </Button>
                </div>
                <p className="mt-2 text-xs text-green-700 dark:text-green-400">
                  Store it securely — you'll need it to verify the <code>Capture-Signature</code> header
                  on every request we send. See the Documentation page for the verification snippet.
                </p>
                <button
                  type="button"
                  onClick={() => setRevealedSecret(null)}
                  className="mt-2 text-xs text-green-700 dark:text-green-400 underline"
                >
                  I've saved it — hide this
                </button>
              </div>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner size="md" />
          </div>
        ) : endpoints.length === 0 ? (
          <EmptyState
            icon={<BoltIcon className="w-12 h-12 text-gray-300 dark:text-secondary-600" />}
            title="No webhook endpoints"
            description="Add an endpoint to start receiving live order, payment, subscription and shipping events for your customers."
            action={
              <Button onClick={() => { setEditing(null); setIsModalOpen(true); }}>
                <PlusIcon className="w-4 h-4 mr-1.5" />
                Add endpoint
              </Button>
            }
          />
        ) : (
          <div className="space-y-4">
            {endpoints.map((endpoint) => (
              <EndpointRow
                key={endpoint.id}
                endpoint={endpoint}
                isBusy={busyId === endpoint.id}
                onEdit={() => { setEditing(endpoint); setIsModalOpen(true); }}
                onDelete={() => setDeleteTarget(endpoint)}
                onToggleEnabled={(enabled) => handleToggleEnabled(endpoint, enabled)}
                onRotateSecret={() => setRotateTarget(endpoint)}
                onTest={() => handleTest(endpoint)}
              />
            ))}
          </div>
        )}
      </div>

      {isModalOpen && (
        <EndpointModal
          key={editing?.id ?? 'new'}
          isOpen
          onClose={() => { setIsModalOpen(false); setEditing(null); }}
          onSubmit={handleSubmit}
          catalog={catalog}
          endpoint={editing}
          isSaving={isSaving}
        />
      )}

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete webhook endpoint"
        message={`Stop sending events to ${deleteTarget?.url ?? 'this endpoint'}? Delivery history for it is removed too. This cannot be undone.`}
        confirmLabel="Delete endpoint"
        variant="danger"
        isLoading={busyId === deleteTarget?.id}
      />

      <ConfirmDialog
        isOpen={Boolean(rotateTarget)}
        onClose={() => setRotateTarget(null)}
        onConfirm={handleRotate}
        title="Rotate signing secret"
        message="The current secret stops working immediately. Any events sent before you deploy the new secret will fail signature verification on your side."
        confirmLabel="Rotate secret"
        variant="warning"
        isLoading={busyId === rotateTarget?.id}
      />
    </Card>
  );
}

export default WebhooksSection;
